import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { type BudgetMember, type Category, type Subcategory, type Transaction } from '../../domain/model';
import { roundShares } from '../../domain/percent';
import { SPENDING_GROUPS, groupOf, summarizeSpending, type SpendingItem } from '../../domain/spending';
import { categoryLabel } from '../categories/labels';
import { EntryRow, type EntryLabels } from '../entries/EntryRow';
import { Amount, CategoryDot, List, ListRow, LoadingState, Section, StatTile, TotalSummary, series, useFormat, type YearMonth } from '../../ui';
import type { HomeSection, HomeSlot } from './layout';
import './home.css';

/**
 * Home on real data, until the analytics phase fills the full dashboard:
 * what was spent this month (spending rule applied: refunds subtract,
 * transfers/income and already-tracked card bills do not count) and the
 * latest entries. Returns null when the month is empty so Home can show
 * its empty state.
 */
export function useMonthEntries(month: YearMonth) {
  const { store, activeSpace, dataVersion } = useServices();
  const [state, setState] = useState<{
    /** The month's newest entries (Recent shows 20). */
    entries: Transaction[];
    /** The month's amounts summed per category and type (every total on Home comes from these). */
    totals: SpendingItem[];
    /** How many entries the month has. */
    count: number;
    categories: Map<string, Category>;
    subcategories: Map<string, Subcategory>;
    /** Everyone who was ever in the budget (a payer who left still has a name). */
    members: Map<string, BudgetMember>;
    /** People in the budget now. */
    memberCount: number;
  } | null>(null);
  useEffect(() => {
    if (!activeSpace) return;
    let live = true;
    const key = `${month.year}-${String(month.month).padStart(2, '0')}`;
    void Promise.all([
      store.transactions.recentInMonth(activeSpace.id, key, 20),
      store.transactions.monthTotals(activeSpace.id, key),
      store.categories.list({ spaceId: activeSpace.id }, { includeDeleted: true }),
      store.subcategories.list({ spaceId: activeSpace.id }, { includeDeleted: true }),
      store.members.list({ spaceId: activeSpace.id }, { includeDeleted: true }),
    ]).then(([entries, totals, cats, subs, members]) => {
      if (!live) return;
      setState({
        entries,
        totals: totals.items,
        count: totals.count,
        categories: new Map(cats.map((c) => [c.id, c])),
        subcategories: new Map(subs.map((s) => [s.id, s])),
        members: new Map(members.map((m) => [m.id, m])),
        memberCount: members.filter((m) => m.deletedAt === null).length,
      });
    });
    return () => {
      live = false;
    };
  }, [store, activeSpace, dataVersion, month.year, month.month]);
  return state;
}

/**
 * Home on real data: the month's total always first, then the sections in
 * the order this device keeps (`layout`), hidden ones left out. Sections
 * built elsewhere (pace, anomalies, bills, trend) come in as `slots`.
 */
export function RecentHome({
  data,
  layout,
  slots,
}: {
  data: NonNullable<ReturnType<typeof useMonthEntries>>;
  layout: readonly HomeSlot[];
  slots: Partial<Record<HomeSection, ReactNode>>;
}) {
  const { t } = useTranslation();
  const { activeSpace } = useServices();
  const { money, percent } = useFormat();
  const summary = summarizeSpending(data.totals, data.categories);
  const total = summary.total;
  const whole = (minor: number) => money(minor, { whole: true });
  // Whole-number shares that add up to 100 (largest remainder).
  const shares = roundShares(summary.byCategory.map((c) => c.total));
  const labels: EntryLabels = {
    categories: data.categories,
    subcategories: data.subcategories,
    members: data.members,
    sharedSpaces: new Set(data.memberCount > 1 && activeSpace ? [activeSpace.id] : []),
  };

  const breakdown = summary.byCategory.length > 0 && (
    <Section title={t('home.thisMonth')}>
      <div className="stats" role="group" aria-label={t('home.groupsLabel')}>
        {SPENDING_GROUPS.map((g) => (
          <StatTile
            key={g}
            label={t(`home.groups.${g}`)}
            value={whole(Math.max(0, summary.byGroup[g]))}
            accent={summary.byCategory
              .filter((c) => groupOf(data.categories.get(c.categoryId)) === g)
              .map((c) => (
                <span key={c.categoryId} style={{ flexGrow: c.total, background: series(data.categories.get(c.categoryId)?.colorSlot ?? 0) }} />
              ))}
          />
        ))}
      </div>
      <List label={t('home.compositionChart')}>
        {summary.byCategory.map((c, i) => {
          const cat = data.categories.get(c.categoryId);
          return (
            <ListRow
              key={c.categoryId}
              density="compact"
              leading={<CategoryDot color={series(cat?.colorSlot ?? 0)} />}
              title={categoryLabel(t, cat)}
              meta={percent(shares[i]! / 100, { signed: false, digits: 0 })}
              trailing={<Amount minor={c.total} />}
            />
          );
        })}
      </List>
    </Section>
  );
  const recent = (
    <Section
      title={t('home.recent')}
      action={
        <Link to="/entries" className="link-button">
          {t('entries.seeAll')}
        </Link>
      }
    >
      <List>
        {data.entries.map((e) => (
          <EntryRow key={e.id} entry={e} labels={labels} />
        ))}
      </List>
    </Section>
  );

  return (
    <>
      <TotalSummary label={t('home.spentSoFar')} minor={Math.max(0, total)} />
      {layout
        .filter((slot) => slot.visible)
        .map((slot) => (
          <Fragment key={slot.key}>{slot.key === 'breakdown' ? breakdown : slot.key === 'recent' ? recent : slots[slot.key]}</Fragment>
        ))}
    </>
  );
}

export function HomeLoading() {
  return <LoadingState variant="hero" />;
}
